/**
 * ストレージラッパー - IndexedDB優先、localStorageフォールバック
 * QuotaExceededErrorを防ぐため、IndexedDBを使用
 */

import localforage from 'localforage';

// IndexedDBインスタンスを作成
const lf = localforage.createInstance({ 
  name: 'aiSommelier',
  storeName: 'appData',
  description: 'AI Sommelier application data storage'
});

export interface StorageResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const storage = {
  /**
   * データを保存（IndexedDB優先、localStorageフォールバック）
   */
  async set<T>(key: string, value: T): Promise<StorageResult<void>> {
    try {
      // IndexedDBに保存を試行
      await lf.setItem(key, value);
      console.log(`✅ [Storage] ${key} をIndexedDBに保存しました`);
      return { success: true };
    } catch (indexedDbError) {
      console.warn(`⚠️ [Storage] IndexedDBに保存できませんでした。localStorageにフォールバック:`, indexedDbError);
      
      try {
        // localStorageにフォールバック
        localStorage.setItem(key, JSON.stringify(value));
        console.log(`✅ [Storage] ${key} をlocalStorageに保存しました`);
        return { success: true };
      } catch (localStorageError) {
        const errorMsg = `両方のストレージに保存できませんでした。IndexedDB: ${indexedDbError}, localStorage: ${localStorageError}`;
        console.error(`❌ [Storage] ${errorMsg}`);
        return { success: false, error: errorMsg };
      }
    }
  },

  /**
   * データを取得（IndexedDB優先、localStorageフォールバック）
   */
  async get<T>(key: string): Promise<StorageResult<T>> {
    try {
      // IndexedDBから取得を試行
      const value = await lf.getItem<T>(key);
      if (value !== null) {
        console.log(`✅ [Storage] ${key} をIndexedDBから取得しました`);
        return { success: true, data: value };
      }
    } catch (indexedDbError) {
      console.warn(`⚠️ [Storage] IndexedDBから取得できませんでした。localStorageにフォールバック:`, indexedDbError);
    }

    try {
      // localStorageから取得を試行
      const raw = localStorage.getItem(key);
      if (raw) {
        const value = JSON.parse(raw) as T;
        console.log(`✅ [Storage] ${key} をlocalStorageから取得しました`);
        return { success: true, data: value };
      }
    } catch (localStorageError) {
      console.error(`❌ [Storage] localStorageから取得エラー:`, localStorageError);
    }

    console.log(`ℹ️ [Storage] ${key} は見つかりませんでした`);
    return { success: false, error: 'Data not found' };
  },

  /**
   * データを削除（両方のストレージから）
   */
  async remove(key: string): Promise<StorageResult<void>> {
    const errors: string[] = [];

    try {
      await lf.removeItem(key);
      console.log(`✅ [Storage] ${key} をIndexedDBから削除しました`);
    } catch (indexedDbError) {
      errors.push(`IndexedDB: ${indexedDbError}`);
      console.warn(`⚠️ [Storage] IndexedDBから削除できませんでした:`, indexedDbError);
    }

    try {
      localStorage.removeItem(key);
      console.log(`✅ [Storage] ${key} をlocalStorageから削除しました`);
    } catch (localStorageError) {
      errors.push(`localStorage: ${localStorageError}`);
      console.warn(`⚠️ [Storage] localStorageから削除できませんでした:`, localStorageError);
    }

    if (errors.length > 0) {
      return { success: false, error: errors.join(', ') };
    }

    return { success: true };
  },

  /**
   * ストレージの状態を確認
   */
  async getStatus(): Promise<{
    indexedDb: boolean;
    localStorage: boolean;
    quota: {
      estimated: number;
      usage: number;
    } | null;
  }> {
    const status = {
      indexedDb: false,
      localStorage: false,
      quota: null as any
    };

    // IndexedDBの状態確認
    try {
      await lf.setItem('test', 'test');
      await lf.removeItem('test');
      status.indexedDb = true;
    } catch (error) {
      console.warn('IndexedDB利用不可:', error);
    }

    // localStorageの状態確認
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      status.localStorage = true;
    } catch (error) {
      console.warn('localStorage利用不可:', error);
    }

    // ストレージクォータの確認（対応ブラウザのみ）
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        status.quota = {
          estimated: estimate.quota || 0,
          usage: estimate.usage || 0
        };
      }
    } catch (error) {
      console.warn('ストレージクォータ情報取得不可:', error);
    }

    return status;
  },

  /**
   * 全データをクリア（両方のストレージから）
   */
  async clear(): Promise<StorageResult<void>> {
    const errors: string[] = [];

    try {
      await lf.clear();
      console.log(`✅ [Storage] IndexedDBをクリアしました`);
    } catch (indexedDbError) {
      errors.push(`IndexedDB: ${indexedDbError}`);
    }

    try {
      localStorage.clear();
      console.log(`✅ [Storage] localStorageをクリアしました`);
    } catch (localStorageError) {
      errors.push(`localStorage: ${localStorageError}`);
    }

    if (errors.length > 0) {
      return { success: false, error: errors.join(', ') };
    }

    return { success: true };
  }
};

// デバッグ用：ストレージ状態をコンソールに出力
if (process.env.NODE_ENV === 'development') {
  storage.getStatus().then(status => {
    console.log('🔍 [Storage Debug] ストレージ状態:', status);
  });
}
